import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Card } from '@/components/ui/card';
import { SchoolzestAIWidget } from '@/components/ui/SchoolzestAIWidget';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Users, Calendar, ClipboardList, CheckCircle, BarChart3, ArrowRight, Clock, Loader2, Mail, UserCircle } from 'lucide-react';

const Dashboard = () => {
  const { token } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Drill-down dialog
  const [drilldownOpen, setDrilldownOpen] = useState(false);
  const [drilldown, setDrilldown] = useState({ card: null, title: '', loading: false, data: null });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Failed to load dashboard stats:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const openDrilldown = async (card, title) => {
    setDrilldownOpen(true);
    setDrilldown({ card, title, loading: true, data: null });
    try {
      const res = await axios.get(`${API}/dashboard/details/${card}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDrilldown({ card, title, loading: false, data: res.data });
    } catch (err) {
      setDrilldown({ card, title, loading: false, data: { error: err.response?.data?.detail || 'Failed to load details' } });
    }
  };

  if (loading) {
    return <div className="text-slate-600">Loading dashboard...</div>;
  }

  const statCards = [
    {
      title: 'Absences Today',
      card: 'absences_today',
      value: stats?.total_absences_today || 0,
      icon: Calendar,
      color: 'text-rose-600',
      bg: 'bg-rose-50',
    },
    {
      title: 'Relief Assigned',
      card: 'relief_today',
      value: stats?.total_relief_assigned || 0,
      icon: ClipboardList,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
    },
    {
      title: 'Pending Acknowledgment',
      card: 'pending_ack',
      value: stats?.pending_assignments || 0,
      icon: CheckCircle,
      color: 'text-slate-600',
      bg: 'bg-slate-100',
    },
    {
      title: 'Active Teachers',
      card: 'active_teachers',
      value: stats?.active_teachers || 0,
      icon: Users,
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
    },
  ];

  const ackStats = stats?.acknowledgement_stats;

  return (
    <div data-testid="dashboard-page">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-slate-900">Dashboard</h1>
        <p className="text-sm sm:text-base text-slate-500 mt-2">Overview of today's relief assignments and absences. Click any card for details.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 sm:mb-8">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <button
              key={card.card}
              type="button"
              onClick={() => openDrilldown(card.card, card.title)}
              className="text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg"
              data-testid={`stat-card-${card.card}`}
            >
              <Card className="dashboard-card p-4 sm:p-6 hover:shadow-md hover:border-blue-300 transition-all duration-200 cursor-pointer h-full">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">{card.title}</p>
                    <p className="text-2xl sm:text-3xl font-bold text-slate-900">{card.value}</p>
                    <p className="mt-2 text-[11px] font-medium text-blue-700 inline-flex items-center gap-1">
                      View details <ArrowRight className="h-3 w-3" />
                    </p>
                  </div>
                  <div className={`p-2 sm:p-3 rounded-md ${card.bg}`}>
                    <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${card.color}`} strokeWidth={1.5} />
                  </div>
                </div>
              </Card>
            </button>
          );
        })}
      </div>

      {/* Acknowledgement Tracking — also interactive */}
      {ackStats && (ackStats.relief_total > 0 || ackStats.invigilation_total > 0) && (
        <button
          type="button"
          onClick={() => openDrilldown('acknowledgement_tracking', 'Acknowledgement Tracking — Pending list')}
          className="block w-full text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg mb-6 sm:mb-8"
          data-testid="ack-tracking-card"
        >
          <Card className="dashboard-card p-4 sm:p-6 hover:shadow-md hover:border-blue-300 transition-all duration-200 cursor-pointer">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-blue-900" strokeWidth={1.5} />
                <h2 className="text-lg sm:text-xl font-semibold tracking-tight text-slate-900">Acknowledgement Tracking</h2>
              </div>
              <span className="text-[11px] font-medium text-blue-700 inline-flex items-center gap-1">
                View who hasn't acknowledged <ArrowRight className="h-3 w-3" />
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 rounded-md border border-slate-200">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Relief Duties</p>
                <div className="flex items-end gap-3 mb-3">
                  <p className="text-2xl font-bold text-slate-900">{ackStats.relief_ack_rate}%</p>
                  <p className="text-sm text-slate-500 mb-0.5">acknowledged</p>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2 mb-2">
                  <div className="bg-emerald-500 h-2 rounded-full transition-all duration-500" style={{ width: `${ackStats.relief_ack_rate}%` }} data-testid="relief-ack-progress" />
                </div>
                <p className="text-xs text-slate-500">{ackStats.relief_acknowledged} of {ackStats.relief_total} assignments</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-md border border-slate-200">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Invigilation Duties</p>
                <div className="flex items-end gap-3 mb-3">
                  <p className="text-2xl font-bold text-slate-900">{ackStats.invigilation_ack_rate}%</p>
                  <p className="text-sm text-slate-500 mb-0.5">acknowledged</p>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2 mb-2">
                  <div className="bg-blue-600 h-2 rounded-full transition-all duration-500" style={{ width: `${ackStats.invigilation_ack_rate}%` }} data-testid="invig-ack-progress" />
                </div>
                <p className="text-xs text-slate-500">{ackStats.invigilation_acknowledged} of {ackStats.invigilation_total} assignments</p>
              </div>
            </div>
          </Card>
        </button>
      )}

      {stats?.recent_absences && stats.recent_absences.length > 0 && (
        <Card className="dashboard-card p-4 sm:p-6" data-testid="recent-absences-card">
          <h2 className="text-lg sm:text-xl font-semibold tracking-tight text-slate-900 mb-4">Recent Absences Today</h2>
          <div className="space-y-3">
            {stats.recent_absences.map((absence) => (
              <div
                key={absence.id}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-slate-50 rounded-md border border-slate-200 gap-2"
                data-testid="absence-item"
              >
                <div>
                  <p className="font-medium text-slate-900">{absence.teacher_name}</p>
                  <p className="text-sm text-slate-500">{absence.is_full_day ? 'Full Day' : `${absence.start_time} - ${absence.end_time}`}</p>
                </div>
                <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded self-start sm:self-auto">
                  {absence.status}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Drill-down Dialog */}
      <Dialog open={drilldownOpen} onOpenChange={setDrilldownOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="dashboard-drilldown-dialog">
          <DialogHeader>
            <DialogTitle>{drilldown.title}</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">Click-through detail from dashboard card.</DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            {drilldown.loading ? (
              <div className="flex items-center gap-2 text-slate-500 text-sm py-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
            ) : drilldown.data?.error ? (
              <div className="text-sm text-rose-600">{drilldown.data.error}</div>
            ) : (
              <DrilldownBody card={drilldown.card} data={drilldown.data} />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* SchoolzestAI Chat */}
      <SchoolzestAIWidget />
    </div>
  );
};

const formatTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-SG', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }); } catch { return iso; }
};

const EmptyState = ({ text }) => (
  <div className="text-sm text-slate-500 italic py-6 text-center">{text}</div>
);

const DrilldownBody = ({ card, data }) => {
  if (!data) return null;

  if (card === 'absences_today') {
    if (!data.items?.length) return <EmptyState text="No absences reported today. 🎉" />;
    return (
      <div className="space-y-2" data-testid="drilldown-absences-today">
        {data.items.map(a => (
          <div key={a.id} className="p-3 bg-slate-50 border border-slate-200 rounded-md flex justify-between items-start gap-3">
            <div>
              <p className="font-semibold text-slate-900">{a.teacher_name}</p>
              <p className="text-xs text-slate-500">{a.is_full_day ? 'Full Day' : `${a.start_time} - ${a.end_time}`} · {(a.reasons || []).join(', ') || '—'}</p>
              {a.created_at && <p className="text-[11px] text-slate-400 inline-flex items-center gap-1 mt-1"><Clock className="h-3 w-3" /> Applied {formatTime(a.created_at)}</p>}
            </div>
            <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-rose-100 text-rose-700 uppercase tracking-wide">{a.status || 'open'}</span>
          </div>
        ))}
      </div>
    );
  }

  if (card === 'relief_today') {
    if (!data.items?.length) return <EmptyState text="No relief assignments today." />;
    return (
      <div className="space-y-2" data-testid="drilldown-relief-today">
        {data.items.map(r => (
          <div key={r.id} className="p-3 bg-slate-50 border border-slate-200 rounded-md">
            <div className="flex justify-between items-start gap-3">
              <div>
                <p className="font-semibold text-slate-900">{r.relief_teacher_name || 'Unassigned'}</p>
                <p className="text-xs text-slate-500">Period {r.period} · {r.class_name || '-'} · {r.subject || '-'}</p>
                <p className="text-xs text-slate-500">Covering: <span className="text-slate-700">{r.absent_teacher_name || '-'}</span></p>
              </div>
              <span className={`px-2 py-0.5 rounded text-[11px] font-medium uppercase tracking-wide ${r.is_acknowledged ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                {r.is_acknowledged ? 'Acknowledged' : 'Pending ack'}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (card === 'pending_ack') {
    const rs = data.relief || [];
    const ivs = data.invigilation || [];
    if (!rs.length && !ivs.length) return <EmptyState text="No pending acknowledgements. 🎉" />;
    return (
      <div className="space-y-4" data-testid="drilldown-pending-ack">
        {rs.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Relief ({rs.length})</p>
            <div className="space-y-2">
              {rs.map(r => (
                <div key={r.id} className="p-3 bg-amber-50/60 border border-amber-100 rounded-md">
                  <p className="text-sm font-semibold text-slate-900">{r.relief_teacher_name || 'Unassigned'} <span className="text-xs text-slate-500">· P{r.period} · {r.class_name}</span></p>
                  <p className="text-xs text-slate-500">{r.date} · Covering {r.absent_teacher_name} · {r.subject}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        {ivs.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Invigilation ({ivs.length})</p>
            <div className="space-y-2">
              {ivs.map(i => (
                <div key={i.id} className="p-3 bg-amber-50/60 border border-amber-100 rounded-md">
                  <p className="text-sm font-semibold text-slate-900">{i.invigilator_name}</p>
                  <p className="text-xs text-slate-500">{i.date} · {i.time_slot} · {i.venue} · {(i.duty_type || '').toUpperCase()}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (card === 'active_teachers') {
    if (!data.items?.length) return <EmptyState text="No active teachers found." />;
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="drilldown-active-teachers">
        {data.items.map(t => (
          <div key={t.id} className="p-3 bg-slate-50 border border-slate-200 rounded-md flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <UserCircle className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-slate-900 truncate">{t.name}</p>
              <p className="text-[11px] text-slate-500 truncate inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {t.email}</p>
            </div>
            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded uppercase bg-slate-200 text-slate-700">{t.role}</span>
          </div>
        ))}
      </div>
    );
  }

  if (card === 'acknowledgement_tracking') {
    const rs = data.relief_pending || [];
    const ivs = data.invigilation_pending || [];
    if (!rs.length && !ivs.length) return <EmptyState text="Everyone has acknowledged their assignments. ✅" />;
    return (
      <div className="space-y-4" data-testid="drilldown-ack-tracking">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Relief — pending ack ({rs.length}/{data.relief_total})</p>
          {rs.length === 0 ? <EmptyState text="All relief duties acknowledged." /> : (
            <div className="space-y-2">
              {rs.map(r => (
                <div key={r.id} className="p-3 bg-amber-50/60 border border-amber-100 rounded-md">
                  <p className="text-sm font-semibold text-slate-900">{r.relief_teacher_name || 'Unassigned'}</p>
                  <p className="text-xs text-slate-500">{r.date} · P{r.period} · {r.class_name} · {r.subject}</p>
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Invigilation — pending ack ({ivs.length}/{data.invigilation_total})</p>
          {ivs.length === 0 ? <EmptyState text="All invigilation duties acknowledged." /> : (
            <div className="space-y-2">
              {ivs.map(i => (
                <div key={i.id} className="p-3 bg-amber-50/60 border border-amber-100 rounded-md">
                  <p className="text-sm font-semibold text-slate-900">{i.invigilator_name}</p>
                  <p className="text-xs text-slate-500">{i.date} · {i.time_slot} · {i.venue} · {(i.duty_type || '').toUpperCase()}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return <pre className="text-xs bg-slate-50 p-3 rounded border">{JSON.stringify(data, null, 2)}</pre>;
};

export default Dashboard;
